// Seat rules the console mirrors from the server (ADR 0033, vd_core::plans::check_assignment):
// a Rep or Support agent needs a seat, and a seat must fit the purchased count. Pure, so the
// rules are unit tested.

import { PLAN_LABEL, PLANS, type Plan, type SeatCount, type SubscriptionStatus } from "@vd/api-client";
import type { Roles } from "../api/types";

export type SeatValue = Plan | "none";

export interface SeatOption {
  value: SeatValue;
  label: string;
  /** "2 of 3", or "4 assigned" with no cap. */
  hint: string;
}

/**
 * "Core", "Core + Marketing", "Marketing" or "No seat": the phone seat and the Marketing seat
 * beside it (the owner, 2026-11-01).
 */
export function seatLabel(plan: Plan | null | undefined, marketing = false): string {
  const phone = plan && plan !== "marketing" ? PLAN_LABEL[plan] : null;
  const withMarketing = marketing || plan === "marketing";
  if (phone && withMarketing) return `${phone} + Marketing`;
  if (phone) return phone;
  return withMarketing ? "Marketing" : "No seat";
}

function countOf(seats: SeatCount[], plan: Plan): SeatCount {
  return seats.find((s) => s.plan === plan) ?? { plan, purchased: null, assigned: 0 };
}

/** "2 of 3" for a capped plan, "2 assigned" for an uncapped one. */
export function seatCountText(c: SeatCount): string {
  return c.purchased === null ? `${c.assigned} assigned` : `${c.assigned} of ${c.purchased}`;
}

/** The picker's options: Basic, Core, Sales and Service, Sales Pro, Sales Max with their counts, then No seat. */
export function seatOptions(seats: SeatCount[]): SeatOption[] {
  return [
    ...PLANS.map((p) => ({
      value: p as SeatValue,
      label: PLAN_LABEL[p],
      hint: seatCountText(countOf(seats, p)),
    })),
    { value: "none", label: "No seat", hint: "" },
  ];
}

/**
 * Why moving a member from `current` to `to` cannot be saved, or `null`. A seat already held
 * does not count against its own plan.
 */
export function seatError(
  roles: Roles,
  current: Plan | null,
  to: Plan | null,
  seats: SeatCount[],
): string | null {
  if (to === null) return roles.rep || roles.support ? "Pick a seat." : null;
  if (to === current) return null;
  const c = countOf(seats, to);
  if (c.purchased !== null && c.assigned >= c.purchased) return `No ${PLAN_LABEL[to]} seats left.`;
  return null;
}

/** The Marketing seat switch's count: "1 of 2", or "1 assigned" with no cap. */
export function marketingCountText(seats: SeatCount[]): string {
  return seatCountText(countOf(seats, "marketing"));
}

/**
 * Why turning the Marketing seat on or off cannot be saved, or `null`. A Rep or Support agent
 * needs a phone seat beside it; a seat already held does not count against itself.
 */
export function marketingSeatError(
  roles: Roles,
  phone: Plan | null,
  current: boolean,
  on: boolean,
  seats: SeatCount[],
): string | null {
  if (!on || current) return null;
  if (phone === null && (roles.rep || roles.support)) return "Pick a seat with a phone.";
  const c = countOf(seats, "marketing");
  if (c.purchased !== null && c.assigned >= c.purchased) return "No Marketing seats left.";
  return null;
}

const SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** "Nov 1": a billing date, read in UTC like the server's periods. */
export function billingDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : SHORT.format(d);
}

/** The when option of a downgrade: "At the end of the period, Nov 1". */
export function periodEndLabel(iso: string): string {
  return `At the end of the period, ${billingDay(iso)}`;
}

export const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: "Trial",
  active: "Active",
  past_due: "Past due",
  suspended: "Suspended",
  canceled: "Canceled",
  internal: "Internal",
};

/** The page's header line: "Active. Oct 1 to Nov 1", or the status alone with no period. */
export function periodLine(status: SubscriptionStatus, start: string | null, end: string | null): string {
  const s = STATUS_LABEL[status];
  return start && end ? `${s}. ${billingDay(start)} to ${billingDay(end)}` : s;
}

/** Share of a meter's limit used, 0 to 1, for the bar. No limit: full when anything is used. */
export function meterShare(used: number, limit: number): number {
  if (limit <= 0) return used > 0 ? 1 : 0;
  return Math.min(1, Math.max(0, used / limit));
}
